import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CasinoIcon from '@mui/icons-material/Casino';
import ClearIcon from '@mui/icons-material/Clear';
import {
  AccountCircle as ProfileIcon,
  AdminPanelSettings as AdminIcon,
  ArrowForward as ArrowForwardIcon,
  Badge as BadgeIcon,
  CheckCircle as CheckIcon,
  Email as EmailIcon,
  Event as EventsIcon,
  FindInPage as LostFoundIcon,
  Groups as ClubsIcon,
  Groups as GroupsIcon,
  Help as DoubtsIcon,
  Lightbulb as SkillsIcon,
  Person as PersonIcon,
  PersonAdd as AddMemberIcon,
  School as SchoolIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import LogoutNavbar from '../../components/LogoutNavbar';
import GlobalSidebar from '../../components/GlobalSidebar';
import PostLoginFooter from '../../components/PostLoginFooter';
import api from '../../api';
import { departmentOptions, yearOptions } from '../../data/academicOptions';
import '../../styles/AdminDashboard.css';
import '../../styles/AddMember.css';

function generateRandomCode(length = 8) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let index = 0; index < length; index += 1) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

const emptyFormData = {
  role: 'club_member',
  first_name: '',
  last_name: '',
  email: '',
  student_id: '',
  year: '',
  college: '',
  department: '',
  club_name: '',
  club_member_id: '',
};

const AddMember = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isCompact = useMediaQuery(theme.breakpoints.down('sm'));

  const [clubs, setClubs] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [loadingClubs, setLoadingClubs] = useState(true);
  const [loadingColleges, setLoadingColleges] = useState(true);
  const [formData, setFormData] = useState(emptyFormData);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [createdUser, setCreatedUser] = useState({ name: '', username: '', clubName: '' });
  const [menuWidths, setMenuWidths] = useState({
    year: null,
    college: null,
    department: null,
    club_name: null,
  });
  const [openSelectField, setOpenSelectField] = useState(null);
  const mainContentRef = useRef(null);

  const sidebarItems = [
    {
      label: 'Profile',
      path: '/admin/profile',
      action: () => navigate('/admin/profile'),
      icon: <ProfileIcon />,
    },
    {
      label: 'Manage Clubs',
      path: '/admin/clubs',
      action: () => navigate('/admin/clubs'),
      icon: <ClubsIcon />,
    },
    {
      label: 'Add Member',
      path: '/admin/add-member',
      action: () => navigate('/admin/add-member'),
      icon: <AddMemberIcon />,
    },
    {
      label: 'Manage Events',
      path: '/admin/events',
      action: () => navigate('/admin/events'),
      icon: <EventsIcon />,
    },
    {
      label: 'Lost and Found',
      path: '/lostfound',
      action: () => navigate('/lostfound'),
      icon: <LostFoundIcon />,
    },
    {
      label: 'Skills',
      path: '/skills',
      action: () => navigate('/skills'),
      icon: <SkillsIcon />,
    },
    {
      label: 'Doubts',
      path: '/yourdoubts',
      action: () => navigate('/yourdoubts'),
      icon: <DoubtsIcon />,
    },
    {
      label: 'Settings',
      path: '/settings',
      action: () => navigate('/settings'),
      icon: <SettingsIcon />,
    },
  ];

  useEffect(() => {
    const fetchClubs = async () => {
      try {
        setLoadingClubs(true);
        const responseData = await api.get('events/club-create-view/');
        const clubsData = Array.isArray(responseData)
          ? responseData
          : Array.isArray(responseData?.data)
            ? responseData.data
            : Array.isArray(responseData?.results)
              ? responseData.results
              : Array.isArray(responseData?.results?.data)
                ? responseData.results.data
                : [];

        const formattedClubs = clubsData
          .map((club) => ({
            id: club?.id ?? 0,
            name: club?.name ?? '',
          }))
          .filter((club) => club.id && club.name);

        setClubs(formattedClubs);
      } catch (error) {
        setClubs([]);
        setSubmitError('Failed to load clubs. Please try again later.');
      } finally {
        setLoadingClubs(false);
      }
    };

    const fetchColleges = async () => {
      try {
        setLoadingColleges(true);
        const response = await api.get('users/college_name/');
        setColleges(Array.isArray(response) ? response : []);
      } catch (error) {
        setColleges([]);
      } finally {
        setLoadingColleges(false);
      }
    };

    fetchClubs();
    fetchColleges();
  }, []);

  const selectedClubObj = useMemo(() => {
    if (!formData.club_name) {
      return null;
    }
    return clubs.find((club) => club.name === formData.club_name) || null;
  }, [clubs, formData.club_name]);

  const validateField = (field, value, state = formData) => {
    const nextErrors = { ...errors };

    switch (field) {
      case 'first_name':
        if (!value.trim()) nextErrors[field] = 'First name is required';
        else if (value.trim().length < 2) nextErrors[field] = 'First name must be at least 2 characters';
        else if (!/^[a-zA-Z\s]+$/.test(value.trim())) nextErrors[field] = 'Only letters and spaces';
        else delete nextErrors[field];
        break;
      case 'last_name':
        if (!value.trim()) nextErrors[field] = 'Last name is required';
        else if (value.trim().length < 2) nextErrors[field] = 'Last name must be at least 2 characters';
        else if (!/^[a-zA-Z\s]+$/.test(value.trim())) nextErrors[field] = 'Only letters and spaces';
        else delete nextErrors[field];
        break;
      case 'email': {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value) nextErrors[field] = 'Email is required';
        else if (!emailRegex.test(value)) nextErrors[field] = 'Enter a valid email address';
        else delete nextErrors[field];
        break;
      }
      case 'student_id':
        if (!value.trim()) nextErrors[field] = 'Student ID is required';
        else if (value.trim().length < 4) nextErrors[field] = 'Student ID must be at least 4 characters';
        else delete nextErrors[field];
        break;
      case 'club_name':
        if (!value.trim()) nextErrors[field] = 'Club is required';
        else delete nextErrors[field];
        break;
      case 'club_member_id':
        if (!value.trim()) nextErrors[field] = 'Club Member ID is required';
        else if (value.trim().length < 3) nextErrors[field] = 'Club Member ID must be at least 3 characters';
        else delete nextErrors[field];
        break;
      case 'year':
      case 'college':
      case 'department':
        if (!value) nextErrors[field] = 'This field is required';
        else delete nextErrors[field];
        break;
      default:
        break;
    }

    setErrors(nextErrors);
    return nextErrors;
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    const nextState = { ...formData, [field]: value };

    if (field === 'club_name' && formData.club_member_id) {
      nextState.club_member_id = '';
      enqueueSnackbar('Unique code cleared. Generate a new code for this club.', { variant: 'info' });
    }

    setFormData(nextState);
    setSubmitError('');

    if (touched[field]) {
      validateField(field, value, nextState);
    }
    if (field === 'club_name' && touched.club_member_id) {
      validateField('club_member_id', nextState.club_member_id, nextState);
    }
  };

  const handleBlur = (field) => (event) => {
    setTouched((current) => ({ ...current, [field]: true }));
    validateField(field, event.target.value);
  };

  const handleGenerateCode = () => {
    setTouched((current) => ({ ...current, email: true, club_name: true, club_member_id: true }));

    const emailValidation = validateField('email', formData.email, formData);
    const clubValidation = validateField('club_name', formData.club_name, formData);
    if (emailValidation.email || clubValidation.club_name) {
      setSubmitError('Enter a valid email and select a club before generating a unique code.');
      return;
    }

    const nextCode = generateRandomCode(8);
    const nextState = { ...formData, club_member_id: nextCode };
    setFormData(nextState);
    validateField('club_member_id', nextCode, nextState);
    setSubmitError('');
  };

  const handleClearCode = () => {
    const nextState = { ...formData, club_member_id: '' };
    setFormData(nextState);
    setTouched((current) => ({ ...current, club_member_id: true }));
    validateField('club_member_id', '', nextState);
  };

  const handleCopyCode = async () => {
    if (!formData.club_member_id) {
      return;
    }
    try {
      await navigator.clipboard.writeText(formData.club_member_id);
      enqueueSnackbar('Unique code copied.', { variant: 'success' });
    } catch (error) {
      setSubmitError('Could not copy code. Please copy it manually.');
    }
  };

  const handleBack = () => {
    navigate('/admin/memberships');
  };

  const handleSelectOpen = (field) => (event) => {
    const width = event.currentTarget?.getBoundingClientRect().width;
    if (width) {
      setMenuWidths((current) => ({ ...current, [field]: width }));
    }
    setOpenSelectField(field);
  };

  const handleSelectClose = () => {
    setOpenSelectField(null);
  };

  const getMenuProps = (field) => {
    const width = menuWidths[field];
    return {
      disablePortal: false,
      disableScrollLock: true,
      container: mainContentRef.current,
      sx: {
        zIndex: 1300,
      },
      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
      transformOrigin: { vertical: 'top', horizontal: 'left' },
      marginThreshold: null,
      transitionDuration: 120,
      PaperProps: {
        sx: {
          mt: 0,
          width: width || undefined,
          minWidth: width || undefined,
          maxWidth: width || undefined,
        },
      },
    };
  };

  const validateAllFields = () => {
    const nextErrors = {};
    Object.entries(formData).forEach(([field, value]) => {
      if (field === 'role') {
        return;
      }

      if (field === 'first_name') {
        if (!value.trim()) nextErrors[field] = 'First name is required';
        else if (value.trim().length < 2) nextErrors[field] = 'First name must be at least 2 characters';
        else if (!/^[a-zA-Z\s]+$/.test(value.trim())) nextErrors[field] = 'Only letters and spaces';
      }

      if (field === 'last_name') {
        if (!value.trim()) nextErrors[field] = 'Last name is required';
        else if (value.trim().length < 2) nextErrors[field] = 'Last name must be at least 2 characters';
        else if (!/^[a-zA-Z\s]+$/.test(value.trim())) nextErrors[field] = 'Only letters and spaces';
      }

      if (field === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value) nextErrors[field] = 'Email is required';
        else if (!emailRegex.test(value)) nextErrors[field] = 'Enter a valid email address';
      }

      if (field === 'student_id') {
        if (!value.trim()) nextErrors[field] = 'Student ID is required';
        else if (value.trim().length < 4) nextErrors[field] = 'Student ID must be at least 4 characters';
      }

      if (field === 'club_name' && !value.trim()) {
        nextErrors[field] = 'Club is required';
      }

      if (field === 'club_member_id') {
        if (!value.trim()) nextErrors[field] = 'Club Member ID is required';
        else if (value.trim().length < 3) nextErrors[field] = 'Club Member ID must be at least 3 characters';
      }

      if ((field === 'year' || field === 'college' || field === 'department') && !value) {
        nextErrors[field] = 'This field is required';
      }

    });

    setErrors(nextErrors);
    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const allTouched = Object.keys(formData).reduce((accumulator, key) => {
      if (key !== 'role') {
        accumulator[key] = true;
      }
      return accumulator;
    }, {});
    setTouched(allTouched);

    const validationErrors = validateAllFields();
    const hasErrors = Object.values(validationErrors).some(Boolean);
    if (hasErrors) {
      setSubmitError('Please correct the highlighted fields before submitting.');
      return;
    }

    if (!selectedClubObj) {
      setSubmitError('Selected club not found. Please choose a valid club.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const membershipPayload = new FormData();
      membershipPayload.append('email', formData.email.trim());
      membershipPayload.append('member_id', formData.club_member_id.trim());
      membershipPayload.append('action', 'add');

      await api.patch(`events/clubmembers/${selectedClubObj.id}/`, membershipPayload);

      const payload = {
        ...formData,
        email: formData.email.trim(),
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        student_id: formData.student_id.trim(),
        club_member_id: formData.club_member_id.trim(),
        role: 'club_member',
      };

      const response = await api.post('users/register/', payload);
      const username = response?.user?.username || response?.username || '';
      const name = `${payload.first_name} ${payload.last_name}`.trim();
      const successMessage = `The Club Account created successfully for club leader - ${name} of ${payload.club_name}.`;

      setCreatedUser({ name, username, clubName: payload.club_name });
      setIsSuccess(true);
      setFormData(emptyFormData);
      setTouched({});
      setErrors({});
      enqueueSnackbar(successMessage, { variant: 'success' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      const message =
        error?.club_member_id?.[0] ||
        error?.club_name?.[0] ||
        error?.email?.[0] ||
        error?.password?.[0] ||
        error?.detail ||
        error?.error ||
        error?.message ||
        'Failed to create member account. Please try again.';
      setSubmitError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="admin-dashboard add-member">
      <LogoutNavbar sidebarItems={isMobile ? sidebarItems : []} />
      <div className="dashboard-layout">
        {!isMobile && <GlobalSidebar />}
        <main
          className="dashboard-main"
          style={{
            marginLeft: isMobile ? 0 : undefined,
            width: isMobile ? '100%' : undefined,
          }}
        >
          <Container ref={mainContentRef} maxWidth="xl" className="dashboard-container">
            <header className="am-page-header">
              <Box className="am-header-left">
                <Box className="am-breadcrumb-row">
                  <Tooltip title="Back">
                    <IconButton onClick={handleBack} className="am-back-icon" size="medium" aria-label="Back">
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Breadcrumbs
                    className="am-breadcrumbs"
                    separator={<NavigateNextIcon fontSize="small" />}
                    aria-label="breadcrumb"
                  >
                    <Link
                      underline="hover"
                      color="inherit"
                      onClick={(clickEvent) => {
                        clickEvent.preventDefault();
                        navigate('/admin/dashboard');
                      }}
                      href="/admin/dashboard"
                      className="am-breadcrumb-link"
                    >
                      Dashboard
                    </Link>
                    <Link
                      underline="hover"
                      color="inherit"
                      onClick={(clickEvent) => {
                        clickEvent.preventDefault();
                        navigate('/admin/memberships');
                      }}
                      href="/admin/memberships"
                      className="am-breadcrumb-link"
                    >
                      Club Members
                    </Link>
                    <Typography color="text.primary" className="am-breadcrumb-current">
                      Add
                    </Typography>
                  </Breadcrumbs>
                </Box>

                <Box className="am-title-wrap">
                  <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" className="am-page-title">
                    Add Club Member
                  </Typography>
                  <Typography variant="body2" className="am-page-subtitle" color="text.secondary">
                    Create the full club-member account and register them against the selected club. The default password will be generated automatically.
                  </Typography>
                </Box>
              </Box>
            </header>

            <Paper className="am-card" elevation={0}>
              <Box className="am-card-header">
                <Box>
                  <Typography variant="h6" className="am-card-title">
                    Main registration
                  </Typography>
                  <Typography className="am-card-subtitle">
                    Complete personal, academic, and club details in one flow.
                  </Typography>
                </Box>
              </Box>

              <Box className="am-card-body">
                {isSuccess ? (
                  <Box sx={{ textAlign: 'center', py: { xs: 2, md: 4 } }}>
                    <Avatar
                      sx={{
                        mx: 'auto',
                        mb: 2,
                        width: 72,
                        height: 72,
                        background: 'linear-gradient(135deg, #016f57, #019a78)',
                        boxShadow: '0 12px 30px rgba(1,111,87,.25)',
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 36, color: '#fff' }} />
                    </Avatar>

                    <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a', mb: 1 }}>
                      Club account created successfully
                    </Typography>

                    {createdUser.name && (
                      <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 800, mb: 1 }}>
                        {createdUser.name}
                      </Typography>
                    )}

                    <Typography variant="body1" sx={{ color: '#64748b', mb: 3, maxWidth: 560, mx: 'auto' }}>
                      {createdUser.username
                        ? `The Club Account created successfully for club leader - ${createdUser.name} of ${createdUser.clubName}. Username: ${createdUser.username}`
                        : `The Club Account created successfully for club leader - ${createdUser.name} of ${createdUser.clubName}.`}
                    </Typography>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={() => {
                          setIsSuccess(false);
                          setCreatedUser({ name: '', username: '', clubName: '' });
                        }}
                        className="am-secondary"
                      >
                        Add Another Member
                      </Button>
                      <Button
                        type="button"
                        variant="contained"
                        onClick={handleBack}
                        className="am-primary"
                        endIcon={<ArrowForwardIcon />}
                      >
                        Back to Members
                      </Button>
                    </Stack>
                  </Box>
                ) : (
                  <Box component="form" onSubmit={handleSubmit}>
                    <Stack spacing={4}>
                      {submitError && (
                        <Alert severity="error" variant="outlined">
                          {submitError}
                        </Alert>
                      )}

                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                          <Avatar sx={{ background: 'linear-gradient(135deg, #016f57 0%, #019a78 100%)' }}>
                            <PersonIcon />
                          </Avatar>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                            Personal Information
                          </Typography>
                        </Box>

                        <Grid container spacing={3}>
                          <Grid item xs={12} md={3}>
                            <TextField
                              label="First Name"
                              placeholder="Enter first name"
                              value={formData.first_name}
                              onChange={handleChange('first_name')}
                              onBlur={handleBlur('first_name')}
                              error={touched.first_name && !!errors.first_name}
                              helperText={touched.first_name && errors.first_name}
                              required
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PersonIcon sx={{ color: '#94a3b8' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} md={3}>
                            <TextField
                              label="Last Name"
                              placeholder="Enter last name"
                              value={formData.last_name}
                              onChange={handleChange('last_name')}
                              onBlur={handleBlur('last_name')}
                              error={touched.last_name && !!errors.last_name}
                              helperText={touched.last_name && errors.last_name}
                              required
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PersonIcon sx={{ color: '#94a3b8' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <TextField
                              label="Email Address"
                              placeholder="your.email@college.edu"
                              value={formData.email}
                              onChange={handleChange('email')}
                              onBlur={handleBlur('email')}
                              error={touched.email && !!errors.email}
                              helperText={touched.email && errors.email}
                              required
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <EmailIcon sx={{ color: '#94a3b8' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Box>

                      <Divider sx={{ borderColor: 'rgba(226,232,240,.8)' }} />

                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                          <Avatar sx={{ background: 'linear-gradient(135deg, #1E3A8A 0%, #8b5cf6 100%)' }}>
                            <SchoolIcon />
                          </Avatar>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                            Academic Details
                          </Typography>
                        </Box>

                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Student ID"
                              placeholder="Enter student ID"
                              value={formData.student_id}
                              onChange={handleChange('student_id')}
                              onBlur={handleBlur('student_id')}
                              error={touched.student_id && !!errors.student_id}
                              helperText={touched.student_id && errors.student_id}
                              required
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <BadgeIcon sx={{ color: '#94a3b8' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              select
                              label="Year of Study"
                              value={formData.year}
                              onChange={handleChange('year')}
                              onBlur={handleBlur('year')}
                              error={touched.year && !!errors.year}
                              helperText={touched.year && errors.year}
                              required
                              fullWidth
                              SelectProps={{
                                MenuProps: getMenuProps('year'),
                                onOpen: handleSelectOpen('year'),
                                onClose: handleSelectClose,
                                open: openSelectField === 'year',
                              }}
                            >
                              {yearOptions.map((year) => (
                                <MenuItem key={year} value={year}>
                                  {year}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              select
                              label="College"
                              value={formData.college}
                              onChange={handleChange('college')}
                              onBlur={handleBlur('college')}
                              error={touched.college && !!errors.college}
                              helperText={
                                touched.college && errors.college
                                  ? errors.college
                                  : loadingColleges
                                    ? 'Loading colleges…'
                                    : ' '
                              }
                              required
                              fullWidth
                              disabled={loadingColleges || colleges.length === 0}
                              SelectProps={{
                                MenuProps: getMenuProps('college'),
                                onOpen: handleSelectOpen('college'),
                                onClose: handleSelectClose,
                                open: openSelectField === 'college',
                              }}
                            >
                              {colleges.map((college) => (
                                <MenuItem key={college.id} value={college.id}>
                                  {college.name}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              select
                              label="Department"
                              value={formData.department}
                              onChange={handleChange('department')}
                              onBlur={handleBlur('department')}
                              error={touched.department && !!errors.department}
                              helperText={touched.department && errors.department}
                              required
                              fullWidth
                              SelectProps={{
                                MenuProps: getMenuProps('department'),
                                onOpen: handleSelectOpen('department'),
                                onClose: handleSelectClose,
                                open: openSelectField === 'department',
                              }}
                            >
                              {departmentOptions.map((department) => (
                                <MenuItem key={department} value={department}>
                                  {department}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        </Grid>
                      </Box>

                      <Divider sx={{ borderColor: 'rgba(226,232,240,.8)' }} />

                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                          <Avatar sx={{ background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)' }}>
                            <GroupsIcon sx={{ fontSize: '1.75rem' }} />
                          </Avatar>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                            Club Information
                          </Typography>
                        </Box>

                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6} lg={5}>
                            <TextField
                              select
                              label="Club"
                              value={formData.club_name}
                              onChange={handleChange('club_name')}
                              onBlur={handleBlur('club_name')}
                              error={touched.club_name && !!errors.club_name}
                              helperText={
                                touched.club_name && errors.club_name
                                  ? errors.club_name
                                  : loadingClubs
                                    ? 'Loading clubs…'
                                    : clubs.length === 0
                                      ? 'No clubs found'
                                      : ' '
                              }
                              required
                              fullWidth
                              disabled={loadingClubs || clubs.length === 0}
                              SelectProps={{
                                MenuProps: getMenuProps('club_name'),
                                onOpen: handleSelectOpen('club_name'),
                                onClose: handleSelectClose,
                                open: openSelectField === 'club_name',
                              }}
                            >
                              {clubs.map((club) => (
                                <MenuItem key={club.id} value={club.name}>
                                  {club.name}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid item xs={12} md={12} lg={7}>
                            <TextField
                              label="Club Member ID"
                              placeholder="Generate or enter a unique code"
                              value={formData.club_member_id}
                              onChange={handleChange('club_member_id')}
                              onBlur={handleBlur('club_member_id')}
                              error={touched.club_member_id && !!errors.club_member_id}
                              helperText={touched.club_member_id && errors.club_member_id}
                              required
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              InputProps={{
                                startAdornment: !isCompact ? (
                                  <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                    <AdminIcon sx={{ color: '#94a3b8', fontSize: '0.85rem' }} />
                                  </InputAdornment>
                                ) : undefined,
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <Tooltip title={formData.club_member_id ? 'Copy code' : 'Generate a code first'}>
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={handleCopyCode}
                                          disabled={!formData.club_member_id}
                                          aria-label="Copy club member code"
                                        >
                                          <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </InputAdornment>
                                ),
                              }}
                              inputProps={{
                                className: 'club-member-id-input',
                                'aria-label': 'club member id',
                              }}
                            />
                          </Grid>
                        </Grid>

                        <Box className="am-inline-actions" sx={{ mt: 2 }}>
                          <Button
                            type="button"
                            variant="outlined"
                            onClick={handleGenerateCode}
                            className="am-secondary"
                            startIcon={<CasinoIcon />}
                            disabled={loadingClubs || isSubmitting}
                          >
                            Generate Code
                          </Button>
                          <Button
                            type="button"
                            variant="outlined"
                            onClick={handleClearCode}
                            className="am-secondary"
                            startIcon={<ClearIcon />}
                            disabled={!formData.club_member_id || isSubmitting}
                          >
                            Clear Code
                          </Button>
                        </Box>
                      </Box>

                      <Divider sx={{ borderColor: 'rgba(226,232,240,.8)' }} />

                      <Box className="am-actions">
                        <Button
                          type="button"
                          variant="outlined"
                          onClick={handleBack}
                          className="am-secondary"
                          disabled={isSubmitting}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          className="am-primary"
                          disabled={isSubmitting || loadingClubs || loadingColleges}
                          endIcon={!isSubmitting ? <ArrowForwardIcon /> : undefined}
                        >
                          {isSubmitting ? 'Creating Account…' : 'Create Club Account'}
                        </Button>
                      </Box>
                    </Stack>
                  </Box>
                )}
              </Box>
            </Paper>
          </Container>
        </main>
      </div>
      <PostLoginFooter />
    </div>
  );
};

export default AddMember;